/**
 * Chat Messages Component
 *
 * Container for displaying chat message list.
 * Handles auto-scrolling and message rendering.
 */

import { LitElement, html, css } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { baseStyles, messageBubbleStyles } from '../styles/shared-styles.js';
import { MESSAGE_TYPES } from '../stores/chat.store.js';
import './message-bubble.js';
import './address-card.js';
import './get-started-card.js';
import './suggestion-box.js';

/**
 * ChatMessages - Message list container
 * @element chat-messages
 * @fires message-feedback - When feedback is given on a message
 */
export class ChatMessages extends LitElement {
    static properties = {
        messages: { type: Array },
        suggestions: { type: Array },
        showFeedback: { type: Boolean, attribute: 'show-feedback' }
    };

    static styles = [
        baseStyles,
        messageBubbleStyles,
        css`
            :host {
                display: block;
                width: 100%;
            }

            /* Chat Messages Container - from eaidev.css lines 173-183 */
            .messages {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                padding-top: 20px;
                padding-bottom: 30px;
            }

            .messages--empty {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 200px;
                color: var(--text-secondary);
                font-style: italic;
            }

            /* Match grouped bot message spacing for address cards */
            address-card {
                margin-top: -6px;
            }
        `
    ];

    constructor() {
        super();
        this.messages = [];
        this.suggestions = [];
        this.showFeedback = false;
    }

    render() {
        if (!this.messages?.length) {
            return html`
                <div class="messages messages--empty">
                    <span>Start a conversation with DAISY</span>
                </div>
            `;
        }

        // Calculate group positions for consecutive bot messages
        const groupInfo = this._calculateMessageGroups();

        return html`
            <div class="messages" role="log" aria-live="polite">
                ${repeat(
                    this.messages,
                    (message) => message.id,
                    (message) => {
                        // Render address card for ADDRESS_CARD type messages
                        if (message.type === MESSAGE_TYPES.ADDRESS_CARD) {
                            // Events bubble up automatically with composed: true
                            return html`
                                <address-card
                                    .addressQuery=${message.text || ''}
                                    ?disabled=${message.disabled}
                                    .messageId=${message.id}
                                    data-message-id=${message.id}
                                >
                                </address-card>
                            `;
                        }
                        // Render get started card for GET_STARTED_CARD type messages
                        if (message.type === MESSAGE_TYPES.GET_STARTED_CARD) {
                            return html`
                                <get-started-card
                                    portal-url=${message.portalUrl || ''}
                                    ?is-loading=${message.isLoading}
                                    error-message=${message.errorMessage || ''}
                                    data-message-id=${message.id}
                                    @get-started-click=${(e) => this._handleGetStartedClick(e, message.id)}
                                >
                                </get-started-card>
                            `;
                        }
                        // Get group position info for this message
                        const info = groupInfo.get(message.id) || { isFirstInGroup: true, isLastInGroup: true };
                        // Render regular message bubble
                        return html`
                            <message-bubble
                                .message=${message}
                                ?show-feedback=${this.showFeedback &&
                                message.sender === 'bot' &&
                                message.type !== 'streaming'}
                                .isFirstInGroup=${info.isFirstInGroup}
                                .isLastInGroup=${info.isLastInGroup}
                                @feedback=${this._handleFeedback}
                            >
                            </message-bubble>
                        `;
                    }
                )}
                ${this.suggestions?.length > 0
                    ? html` <suggestion-box .suggestions=${this.suggestions}> </suggestion-box> `
                    : ''}
            </div>
        `;
    }

    /**
     * Calculate message group positions for consecutive bot messages
     * Returns a Map with message ID -> { isFirstInGroup, isLastInGroup }
     * @returns {Map<string, {isFirstInGroup: boolean, isLastInGroup: boolean}>}
     */
    _calculateMessageGroups() {
        const groupInfo = new Map();

        for (let i = 0; i < this.messages.length; i++) {
            const msg = this.messages[i];

            // Skip non-bot messages or special types
            if (
                msg.sender !== 'bot' ||
                msg.type === MESSAGE_TYPES.ADDRESS_CARD ||
                msg.type === MESSAGE_TYPES.GET_STARTED_CARD
            ) {
                groupInfo.set(msg.id, { isFirstInGroup: true, isLastInGroup: true });
                continue;
            }

            // Check previous message
            const prevMsg = i > 0 ? this.messages[i - 1] : null;
            const isPrevBotMessage =
                prevMsg &&
                prevMsg.sender === 'bot' &&
                prevMsg.type !== MESSAGE_TYPES.ADDRESS_CARD &&
                prevMsg.type !== MESSAGE_TYPES.GET_STARTED_CARD;

            // Check next message
            const nextMsg = i < this.messages.length - 1 ? this.messages[i + 1] : null;
            const isNextBotMessage =
                nextMsg &&
                nextMsg.sender === 'bot' &&
                nextMsg.type !== MESSAGE_TYPES.ADDRESS_CARD &&
                nextMsg.type !== MESSAGE_TYPES.GET_STARTED_CARD;

            groupInfo.set(msg.id, {
                isFirstInGroup: !isPrevBotMessage,
                isLastInGroup: !isNextBotMessage
            });
        }

        return groupInfo;
    }

    /**
     * Handle feedback from message bubble
     * @param {CustomEvent} e - Feedback event
     */
    _handleFeedback(e) {
        this.dispatchEvent(
            new CustomEvent('message-feedback', {
                detail: e.detail,
                bubbles: true,
                composed: true
            })
        );
    }

    /**
     * Handle get started card click
     * @param {CustomEvent} e - Click event from get-started-card
     * @param {string} messageId - Message ID to remove
     */
    _handleGetStartedClick(e, messageId) {
        this.dispatchEvent(
            new CustomEvent('get-started-click', {
                detail: {
                    ...e.detail,
                    messageId
                },
                bubbles: true,
                composed: true
            })
        );
    }
}

customElements.define('chat-messages', ChatMessages);